/**
 * Be Understood Tutorial Page (Me mode with voice, desktop app + KizunaAI)
 *
 * Screenshots were taken from the Sokuji v0.44.0 desktop app on Windows; the ones
 * not retaken for v0.44.0 are from v0.41.1.
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const en: TutorialData = {
  pageTitle: 'Speak in Meetings in Another Language',
  backLink: 'Back to KizunaAI Quick Start',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Overview',
    content:
      'This guide sets up the Sokuji desktop app so that the people in your meeting hear what you say, spoken in their language. You talk in your own language; Sokuji translates it and speaks it into the meeting through a virtual microphone. It works with any meeting app that lets you choose a microphone, including the Microsoft Teams and Zoom desktop apps, and uses KizunaAI (Powered by Soniox; the Qwen engine works the same way). The screenshots are from Sokuji 0.44.0 on Windows, speaking Chinese to an English-speaking meeting.',
  },
  steps: [
    {
      title: 'Set up Sokuji with KizunaAI',
      content:
        'If you have not done so yet, follow the <a href="/docs/kizuna-ai/quick-start">KizunaAI quick start</a> to install Sokuji and sign in.',
    },
    {
      title: 'Choose “Be understood in a meeting”',
      content:
        'In the setup guide, choose <strong>Be understood in a meeting</strong>. It puts Sokuji in <strong>Me</strong> mode with voice and subtitles. If you have already finished the setup, click <strong>Me</strong> in the mode switch at the bottom left of the main window instead.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Set the two languages',
      content:
        'Set <strong>I speak</strong> to your own language and <strong>they hear</strong> to the language of the people in the meeting. Here: I speak Chinese, they hear English.',
      screenshot: '02-languages.png',
    },
    {
      title: 'Install the virtual audio cable',
      content:
        'Your translated voice reaches the meeting through a virtual microphone. On Windows this is VB-CABLE, a free driver. While it is missing, Sokuji asks to install it each time it starts: click <strong>Install Now</strong>, and restart Windows if it asks you to. From then on, Sokuji sends its translated voice to VB-CABLE by itself.',
      screenshot: '03-virtual-cable-tip.png',
      tip: 'On macOS the Sokuji installer adds a virtual audio driver, and on Linux Sokuji creates its own virtual microphone. The rest of this guide is the same.',
    },
    {
      title: 'Choose your microphone',
      content:
        'In Settings, under <strong>Microphone</strong>, pick your real microphone. In the <strong>Speech</strong> block, <strong>Translation the other side hears</strong> is on and goes to the virtual microphone, <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. <strong>I hear it too</strong> is optional: switch it on and choose your headset beside it if you want to hear the translated voice yourself.',
      screenshot: '04-devices.png',
    },
    {
      title: 'Choose VB-CABLE as the microphone in the meeting app',
      content:
        'In Microsoft Teams, open <strong>Settings → Devices</strong> and set the microphone to <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. In Zoom the same setting is under <strong>Settings → Audio → Microphone</strong>. Leave the speaker on your usual headset or speakers.',
      tip: 'Your real microphone is selected in Sokuji, not in the meeting app: Sokuji listens to you, and the meeting app receives only the translated voice.',
    },
    {
      title: 'Start speaking',
      content:
        'Click <strong>Start</strong> in Sokuji and speak in your own language. The people in the meeting hear the translation in their language, and Sokuji shows what you said and its translation as subtitles.',
      screenshot: '05-ready-to-start.png',
    },
  ],
  tips: {
    title: 'Tips for Best Results',
    items: [
      'Make a short test call first, for example with a colleague, and check that they hear the translated voice.',
      'Pause briefly at the end of each sentence: the translation is spoken once you finish a thought.',
      'Wear headphones, so that the meeting’s sound does not reach your microphone.',
    ],
  },
  faq: {
    title: 'Frequently Asked Questions',
    items: [
      {
        question: 'Does the other person need to install anything?',
        answer: 'No. Only you run Sokuji. The other person just hears a microphone that speaks their language.',
      },
      {
        question: 'Can I also understand what they say?',
        answer: 'Yes. Switch to Both mode and follow the two-way meeting guide: Sokuji then also translates the other side for you, as subtitles and, if you want, read aloud.',
      },
      {
        question: 'Can I choose the voice?',
        answer: 'Yes. In the Speech block, click the voice name under Translation the other side hears (for example Voice Adrian). It opens Voice Settings, where you can pick another voice or clone your own.',
      },
      {
        question: 'The other person hears nothing. What should I check?',
        answer:
          'Check that the meeting app’s microphone is CABLE Output (VB-Audio Virtual Cable), that Translation the other side hears is switched on in Sokuji, and that a session is running.',
      },
    ],
  },
  troubleshooting: {
    title: 'Troubleshooting',
    content:
      'Still stuck? Send us feedback from the account menu, or write to <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. You can also ask in <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const zh: TutorialData = {
  pageTitle: '在会议中用另一种语言发言',
  backLink: '返回 KizunaAI 快速入门',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '概述',
    content: '本指南介绍如何设置 Sokuji 桌面应用，让会议中的其他人以他们的语言听到你说的话。你用自己的语言说话；Sokuji 将其翻译后，通过虚拟麦克风在会议中说出来。它适用于任何可以选择麦克风的会议应用，包括 Microsoft Teams 和 Zoom 桌面应用，并使用 KizunaAI（Soniox 驱动；Qwen 引擎的用法相同）。截图来自 Windows 上的 Sokuji 0.44.0，用中文向说英语的会议发言。',
  },
  steps: [
    {
      title: '用 KizunaAI 设置 Sokuji',
      content: '如果还没有完成，请按照 <a href="/docs/kizuna-ai/quick-start">KizunaAI 快速入门</a>安装 Sokuji 并登录。',
    },
    {
      title: '选择“让对方在会议里听懂我”',
      content: '在设置向导中选择<strong>让对方在会议里听懂我</strong>。它会让 Sokuji 进入带语音和字幕的<strong>我</strong>模式。如果已经完成设置，也可以点击主窗口左下角模式切换中的<strong>我</strong>。',
      screenshot: '01-scenario.png',
    },
    {
      title: '设置两种语言',
      content: '将<strong>我说</strong>设为你自己的语言，将<strong>对方听到</strong>设为会议中其他人的语言。此处：我说中文，对方听到英语。',
      screenshot: '02-languages.png',
    },
    {
      title: '安装虚拟音频线',
      content: '你的翻译语音通过虚拟麦克风传入会议。在 Windows 上，它就是免费驱动 VB-CABLE。只要尚未安装，Sokuji 每次启动都会询问是否安装：点击 <strong>Install Now</strong>，如果系统提示则重启 Windows。此后，Sokuji 会自动把翻译语音发送到 VB-CABLE。',
      screenshot: '03-virtual-cable-tip.png',
      tip: '在 macOS 上，Sokuji 安装程序会添加一个虚拟音频驱动；在 Linux 上，Sokuji 会自行创建虚拟麦克风。本指南的其余步骤相同。',
    },
    {
      title: '选择麦克风',
      content: '在设置的<strong>麦克风</strong>下选择你的真实麦克风。在<strong>语音</strong>部分，<strong>对方听到的翻译</strong>已打开，输出到虚拟麦克风 <strong>CABLE Output (VB-Audio Virtual Cable)</strong>。<strong>我也听</strong>是可选的：如果你想自己也听到翻译语音，就打开它，并在旁边选择你的耳机。',
      screenshot: '04-devices.png',
    },
    {
      title: '在会议应用中选择 VB-CABLE 作为麦克风',
      content: '在 Microsoft Teams 中打开<strong>设置 → 设备</strong>，将麦克风设为 <strong>CABLE Output (VB-Audio Virtual Cable)</strong>。在 Zoom 中，同样的设置位于<strong>设置 → 音频 → 麦克风</strong>。扬声器保持你平时使用的耳机或音箱即可。',
      tip: '真实麦克风是在 Sokuji 中选择的，而不是在会议应用中：Sokuji 收听你的声音，会议应用只接收翻译后的语音。',
    },
    {
      title: '开始说话',
      content: '在 Sokuji 中点击<strong>开始</strong>，用你自己的语言说话。会议中的人会听到用他们的语言说出的翻译，Sokuji 则以字幕显示你说的话及其翻译。',
      screenshot: '05-ready-to-start.png',
    },
  ],
  tips: {
    title: '最佳使用建议',
    items: [
      '先进行一次简短的测试通话（例如和同事），确认对方能听到翻译语音。',
      '每句话结束时稍作停顿：一段话说完后，翻译才会被朗读出来。',
      '请戴耳机，以免会议的声音传入你的麦克风。',
    ],
  },
  faq: {
    title: '常见问题',
    items: [
      {
        question: '对方需要安装什么吗？',
        answer: '不需要。只有你运行 Sokuji。对方只会听到一个说着他们语言的麦克风。',
      },
      {
        question: '我也能听懂他们说的话吗？',
        answer: '可以。切换到两者模式，并按照双向会议指南操作：Sokuji 还会把对方的话翻译给你，以字幕显示，也可以按你的需要朗读出来。',
      },
      {
        question: '我可以选择语音吗？',
        answer: '可以。在“语音”部分，点击“对方听到的翻译”下方的声音名称（例如“声音 Adrian”）。它会打开“语音设置”，你可以在那里换一个声音，或者克隆你自己的声音。',
      },
      {
        question: '对方什么也听不到，应该检查什么？',
        answer: '检查会议应用的麦克风是否为 CABLE Output (VB-Audio Virtual Cable)，Sokuji 中的“对方听到的翻译”是否已打开，以及会话是否正在运行。',
      },
    ],
  },
  troubleshooting: {
    title: '故障排除',
    content: '仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>。你也可以在 <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> 中提问。',
  },
};

const ja: TutorialData = {
  pageTitle: '会議で別の言語で話す',
  backLink: 'KizunaAI クイックスタートに戻る',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '概要',
    content: 'このガイドでは、会議の参加者があなたの話を自分の言語で聞けるように Sokuji デスクトップアプリを設定します。あなたは自分の言語で話し、Sokuji がそれを翻訳して、仮想マイクを通じて会議に音声で届けます。Microsoft Teams や Zoom のデスクトップアプリを含め、マイクを選択できる会議アプリならどれでも使え、KizunaAI（Soniox 提供。Qwen エンジンでも同じ手順です）を使用します。スクリーンショットは Windows 版 Sokuji 0.44.0 のもので、英語の会議に向けて中国語で話しています。',
  },
  steps: [
    {
      title: 'KizunaAI で Sokuji をセットアップする',
      content: 'まだの場合は、<a href="/docs/kizuna-ai/quick-start">KizunaAI クイックスタート</a>に沿って Sokuji をインストールし、サインインしてください。',
    },
    {
      title: '「会議で自分の話を伝える」を選ぶ',
      content: 'セットアップガイドで<strong>会議で自分の話を伝える</strong>を選びます。Sokuji が音声と字幕を使う<strong>自分</strong>モードになります。すでにセットアップを終えている場合は、代わりにメイン画面左下のモード切り替えで<strong>自分</strong>をクリックします。',
      screenshot: '01-scenario.png',
    },
    {
      title: '2 つの言語を設定する',
      content: '<strong>話す言語</strong>を自分の言語に、<strong>相手が聞く言語</strong>を会議の参加者の言語に設定します。ここでは、話す言語は中国語、相手が聞く言語は英語です。',
      screenshot: '02-languages.png',
    },
    {
      title: '仮想オーディオケーブルをインストールする',
      content: '翻訳されたあなたの声は、仮想マイクを通じて会議に届きます。Windows では無料のドライバー VB-CABLE がその役割を担います。未インストールの間は、Sokuji が起動のたびにインストールを勧めます。<strong>Install Now</strong> をクリックし、求められたら Windows を再起動してください。以降は、Sokuji が翻訳音声を自動で VB-CABLE に送ります。',
      screenshot: '03-virtual-cable-tip.png',
      tip: 'macOS では Sokuji のインストーラーが仮想オーディオドライバーを追加し、Linux では Sokuji が独自の仮想マイクを作成します。このガイドの残りの手順は同じです。',
    },
    {
      title: 'マイクを選ぶ',
      content: '設定の<strong>マイクロフォン</strong>で実際のマイクを選びます。<strong>音声</strong>セクションでは<strong>相手に聞こえる翻訳</strong>がオンになっていて、仮想マイク <strong>CABLE Output (VB-Audio Virtual Cable)</strong> に出力されます。<strong>自分にも再生</strong>は任意です。翻訳音声を自分でも聞きたい場合はオンにして、その横でヘッドセットを選びます。',
      screenshot: '04-devices.png',
    },
    {
      title: '会議アプリのマイクに VB-CABLE を選ぶ',
      content: 'Microsoft Teams で<strong>設定 → デバイス</strong>を開き、マイクを <strong>CABLE Output (VB-Audio Virtual Cable)</strong> に設定します。Zoom では同じ設定が<strong>設定 → オーディオ → マイク</strong>にあります。スピーカーはいつものヘッドセットやスピーカーのままにしておきます。',
      tip: '実際のマイクは会議アプリではなく Sokuji で選びます。Sokuji があなたの声を聞き、会議アプリには翻訳された音声だけが届きます。',
    },
    {
      title: '話し始める',
      content: 'Sokuji で<strong>開始</strong>をクリックし、自分の言語で話します。会議の参加者には翻訳が相手の言語で聞こえ、Sokuji にはあなたの発言とその翻訳が字幕で表示されます。',
      screenshot: '05-ready-to-start.png',
    },
  ],
  tips: {
    title: '最良の結果を得るためのヒント',
    items: [
      'まず同僚などと短いテスト通話をして、翻訳音声が相手に聞こえるか確認してください。',
      '文の終わりで少し間を置いてください。ひとまとまりの話を終えた時点で翻訳が読み上げられます。',
      '会議の音がマイクに入らないよう、ヘッドホンを着けてください。',
    ],
  },
  faq: {
    title: 'よくある質問',
    items: [
      {
        question: '相手も何かをインストールする必要がありますか？',
        answer: 'いいえ。Sokuji を動かすのはあなただけです。相手には、相手の言語を話すマイクの音声が聞こえるだけです。',
      },
      {
        question: '相手の話も理解できますか？',
        answer: 'はい。両方モードに切り替えて双方向の会議ガイドに沿って設定すると、Sokuji が相手の発言もあなた向けに翻訳して字幕で表示し、希望すれば読み上げもします。',
      },
      {
        question: '声を選べますか？',
        answer: 'はい。「音声」セクションで、「相手に聞こえる翻訳」の下にある音声名（例：「音声 Adrian」）をクリックします。「音声設定」が開き、別の音声を選んだり、自分の声を複製したりできます。',
      },
      {
        question: '相手に何も聞こえません。何を確認すればよいですか？',
        answer: '会議アプリのマイクが CABLE Output (VB-Audio Virtual Cable) になっているか、Sokuji で「相手に聞こえる翻訳」がオンになっているか、セッションが実行中かを確認してください。',
      },
    ],
  },
  troubleshooting: {
    title: 'トラブルシューティング',
    content: 'それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href="mailto:support@kizuna.ai">support@kizuna.ai</a> までご連絡ください。<a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> で質問することもできます。',
  },
};

const ko: TutorialData = {
  pageTitle: '회의에서 다른 언어로 말하기',
  backLink: 'KizunaAI 빠른 시작으로 돌아가기',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '개요',
    content: '이 가이드는 회의 참가자들이 내 말을 자신의 언어로 들을 수 있도록 Sokuji 데스크톱 앱을 설정합니다. 나는 내 언어로 말하고, Sokuji가 이를 번역해 가상 마이크를 통해 회의에 음성으로 전달합니다. Microsoft Teams와 Zoom 데스크톱 앱을 포함해 마이크를 선택할 수 있는 모든 회의 앱에서 작동하며, KizunaAI(Soniox 제공이며, Qwen 엔진도 같은 방식으로 작동합니다)를 사용합니다. 스크린샷은 Windows용 Sokuji 0.44.0에서 촬영했으며, 영어로 진행되는 회의에서 중국어로 말하는 예입니다.',
  },
  steps: [
    {
      title: 'KizunaAI로 Sokuji 설정',
      content: '아직 하지 않았다면 <a href="/docs/kizuna-ai/quick-start">KizunaAI 빠른 시작</a>을 따라 Sokuji를 설치하고 로그인하세요.',
    },
    {
      title: '“회의에서 내 말 전달하기” 선택',
      content: '설정 가이드에서 <strong>회의에서 내 말 전달하기</strong>를 선택하세요. Sokuji가 음성과 자막을 사용하는 <strong>나</strong> 모드로 설정됩니다. 이미 설정을 마쳤다면 대신 메인 창 왼쪽 아래의 모드 전환에서 <strong>나</strong>를 클릭하세요.',
      screenshot: '01-scenario.png',
    },
    {
      title: '두 언어 설정',
      content: '<strong>내가 말하는 언어</strong>를 내 언어로, <strong>상대방이 듣는 언어</strong>를 회의 참가자들의 언어로 설정하세요. 여기서는 내가 말하는 언어는 중국어, 상대방이 듣는 언어는 영어입니다.',
      screenshot: '02-languages.png',
    },
    {
      title: '가상 오디오 케이블 설치',
      content: '번역된 내 음성은 가상 마이크를 통해 회의에 전달됩니다. Windows에서는 무료 드라이버인 VB-CABLE이 이 역할을 합니다. 설치되지 않은 동안에는 Sokuji가 시작될 때마다 설치를 권합니다. <strong>Install Now</strong>를 클릭하고, 요청하면 Windows를 다시 시작하세요. 그 이후로는 Sokuji가 번역된 음성을 알아서 VB-CABLE로 보냅니다.',
      screenshot: '03-virtual-cable-tip.png',
      tip: 'macOS에서는 Sokuji 설치 프로그램이 가상 오디오 드라이버를 추가하고, Linux에서는 Sokuji가 자체 가상 마이크를 만듭니다. 이 가이드의 나머지 단계는 같습니다.',
    },
    {
      title: '마이크 선택',
      content: '설정의 <strong>마이크</strong>에서 실제 마이크를 고르세요. <strong>음성</strong> 섹션에서는 <strong>상대방에게 들리는 번역</strong>이 켜져 있으며, 가상 마이크인 <strong>CABLE Output (VB-Audio Virtual Cable)</strong>로 보내집니다. <strong>나도 듣기</strong>는 선택 사항입니다. 번역된 음성을 직접 듣고 싶다면 이를 켜고 옆에서 헤드셋을 고르세요.',
      screenshot: '04-devices.png',
    },
    {
      title: '회의 앱에서 VB-CABLE을 마이크로 선택',
      content: 'Microsoft Teams에서 <strong>설정 → 장치</strong>를 열고 마이크를 <strong>CABLE Output (VB-Audio Virtual Cable)</strong>로 설정하세요. Zoom에서는 같은 설정이 <strong>설정 → 오디오 → 마이크</strong>에 있습니다. 스피커는 평소 쓰는 헤드셋이나 스피커 그대로 두세요.',
      tip: '실제 마이크는 회의 앱이 아니라 Sokuji에서 선택합니다. Sokuji가 내 목소리를 듣고, 회의 앱은 번역된 음성만 받습니다.',
    },
    {
      title: '말하기 시작',
      content: 'Sokuji에서 <strong>시작</strong>을 클릭하고 내 언어로 말하세요. 회의 참가자들은 번역을 자신의 언어로 듣고, Sokuji는 내가 한 말과 그 번역을 자막으로 보여 줍니다.',
      screenshot: '05-ready-to-start.png',
    },
  ],
  tips: {
    title: '최상의 결과를 위한 팁',
    items: [
      '먼저 동료 등과 짧은 테스트 통화를 해서 상대방에게 번역된 음성이 들리는지 확인하세요.',
      '각 문장이 끝날 때 잠깐 멈추세요. 하나의 생각을 마쳐야 번역이 음성으로 나옵니다.',
      '회의 소리가 마이크에 들어가지 않도록 헤드폰을 착용하세요.',
    ],
  },
  faq: {
    title: '자주 묻는 질문',
    items: [
      {
        question: '상대방도 무언가를 설치해야 하나요?',
        answer: '아니요. Sokuji는 나만 실행합니다. 상대방은 자신의 언어로 말하는 마이크 소리를 들을 뿐입니다.',
      },
      {
        question: '상대방의 말도 이해할 수 있나요?',
        answer: '네. 모두 모드로 전환하고 양방향 회의 가이드를 따르면, Sokuji가 상대방의 말도 나를 위해 번역합니다. 자막으로 보여 주고, 원하면 소리 내어 읽어 줍니다.',
      },
      {
        question: '음성을 고를 수 있나요?',
        answer: '네. “음성” 섹션에서 “상대방에게 들리는 번역” 아래의 음성 이름(예: 음성 Adrian)을 클릭하세요. “음성 설정”이 열리며, 여기서 다른 음성을 고르거나 내 음성을 복제할 수 있습니다.',
      },
      {
        question: '상대방에게 아무것도 들리지 않습니다. 무엇을 확인해야 하나요?',
        answer: '회의 앱의 마이크가 CABLE Output (VB-Audio Virtual Cable)로 되어 있는지, Sokuji에서 “상대방에게 들리는 번역”이 켜져 있는지, 세션이 실행 중인지 확인하세요.',
      },
    ],
  },
  troubleshooting: {
    title: '문제 해결',
    content: '그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>에서 질문할 수도 있습니다.',
  },
};

const es: TutorialData = {
  pageTitle: 'Habla en reuniones en otro idioma',
  backLink: 'Volver al inicio rápido de KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Descripción general',
    content: 'Esta guía configura la aplicación de escritorio de Sokuji para que los participantes de tu reunión oigan lo que dices, hablado en su idioma. Tú hablas en tu propio idioma; Sokuji lo traduce y lo pronuncia en la reunión a través de un micrófono virtual. Funciona con cualquier aplicación de reuniones que te deje elegir un micrófono, incluidas las aplicaciones de escritorio de Microsoft Teams y Zoom, y usa KizunaAI (Con tecnología de Soniox; el motor de Qwen funciona igual). Las capturas son de Sokuji 0.44.0 en Windows, hablando en chino en una reunión en inglés.',
  },
  steps: [
    {
      title: 'Configura Sokuji con KizunaAI',
      content: 'Si aún no lo has hecho, sigue el <a href="/docs/kizuna-ai/quick-start">inicio rápido de KizunaAI</a> para instalar Sokuji e iniciar sesión.',
    },
    {
      title: 'Elige «Que te entiendan en una reunión»',
      content: 'En la guía de configuración, elige <strong>Que te entiendan en una reunión</strong>. Pone Sokuji en modo <strong>Yo</strong>, con voz y subtítulos. Si ya terminaste la configuración, haz clic en <strong>Yo</strong> en el selector de modo, abajo a la izquierda de la ventana principal.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Configura los dos idiomas',
      content: 'Pon en <strong>Yo hablo</strong> tu propio idioma y en <strong>ellos escuchan</strong> el idioma de los participantes de la reunión. Aquí: Yo hablo Chino, ellos escuchan Inglés.',
      screenshot: '02-languages.png',
    },
    {
      title: 'Instala el cable de audio virtual',
      content: 'Tu voz traducida llega a la reunión a través de un micrófono virtual. En Windows es VB-CABLE, un controlador gratuito. Mientras no esté instalado, Sokuji te ofrece instalarlo cada vez que arranca: haz clic en <strong>Install Now</strong> y reinicia Windows si te lo pide. A partir de entonces, Sokuji envía su voz traducida a VB-CABLE por sí solo.',
      screenshot: '03-virtual-cable-tip.png',
      tip: 'En macOS, el instalador de Sokuji añade un controlador de audio virtual, y en Linux Sokuji crea su propio micrófono virtual. El resto de esta guía es igual.',
    },
    {
      title: 'Elige tu micrófono',
      content: 'En Configuración, en <strong>Micrófono</strong>, elige tu micrófono real. En la sección <strong>Voz</strong>, <strong>Traducción que oye la otra parte</strong> está activada y va al micrófono virtual, <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. <strong>Yo también la oigo</strong> es opcional: actívalo y elige tus auriculares a su lado si quieres oír tú también la voz traducida.',
      screenshot: '04-devices.png',
    },
    {
      title: 'Elige VB-CABLE como micrófono en la aplicación de reuniones',
      content: 'En Microsoft Teams, abre <strong>Configuración → Dispositivos</strong> y elige como micrófono <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. En Zoom, el mismo ajuste está en <strong>Configuración → Audio → Micrófono</strong>. Deja el altavoz en tus auriculares o altavoces habituales.',
      tip: 'Tu micrófono real se elige en Sokuji, no en la aplicación de reuniones: Sokuji te escucha a ti, y la aplicación de reuniones recibe solo la voz traducida.',
    },
    {
      title: 'Empieza a hablar',
      content: 'Haz clic en <strong>Iniciar</strong> en Sokuji y habla en tu propio idioma. Los participantes de la reunión oyen la traducción en su idioma, y Sokuji muestra lo que dijiste y su traducción como subtítulos.',
      screenshot: '05-ready-to-start.png',
    },
  ],
  tips: {
    title: 'Consejos para obtener mejores resultados',
    items: [
      'Haz antes una llamada de prueba corta, por ejemplo con un compañero, y comprueba que oye la voz traducida.',
      'Haz una breve pausa al final de cada frase: la traducción se pronuncia cuando terminas una idea.',
      'Usa auriculares, para que el sonido de la reunión no llegue a tu micrófono.',
    ],
  },
  faq: {
    title: 'Preguntas frecuentes',
    items: [
      {
        question: '¿La otra persona tiene que instalar algo?',
        answer: 'No. Solo tú usas Sokuji. La otra persona simplemente oye un micrófono que habla su idioma.',
      },
      {
        question: '¿También puedo entender lo que dicen ellos?',
        answer: 'Sí. Cambia al modo Ambos y sigue la guía de reuniones bidireccionales: Sokuji también traducirá para ti lo que dice la otra parte, como subtítulos y, si quieres, leído en voz alta.',
      },
      {
        question: '¿Puedo elegir la voz?',
        answer: 'Sí. En la sección Voz, haz clic en el nombre de la voz bajo Traducción que oye la otra parte (por ejemplo, Voz Adrian). Se abre Configuración de Voz, donde puedes elegir otra voz o clonar la tuya.',
      },
      {
        question: 'La otra persona no oye nada. ¿Qué debo comprobar?',
        answer: 'Comprueba que el micrófono de la aplicación de reuniones sea CABLE Output (VB-Audio Virtual Cable), que Traducción que oye la otra parte esté activada en Sokuji y que haya una sesión en marcha.',
      },
    ],
  },
  troubleshooting: {
    title: 'Solución de problemas',
    content: '¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. También puedes preguntar en <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const fr: TutorialData = {
  pageTitle: 'Parler en réunion dans une autre langue',
  backLink: 'Retour au démarrage rapide KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Aperçu',
    content: 'Ce guide configure l’application de bureau Sokuji pour que les participants à votre réunion entendent ce que vous dites, prononcé dans leur langue. Vous parlez dans votre propre langue ; Sokuji le traduit et le prononce dans la réunion par un microphone virtuel. Il fonctionne avec toute application de réunion qui vous laisse choisir un microphone, y compris les applications de bureau Microsoft Teams et Zoom, et utilise KizunaAI (Propulsé par Soniox ; le moteur Qwen fonctionne de la même façon). Les captures d’écran proviennent de Sokuji 0.44.0 sous Windows, en parlant chinois dans une réunion en anglais.',
  },
  steps: [
    {
      title: 'Configurez Sokuji avec KizunaAI',
      content: 'Si ce n’est pas encore fait, suivez le <a href="/docs/kizuna-ai/quick-start">démarrage rapide KizunaAI</a> pour installer Sokuji et vous connecter.',
    },
    {
      title: 'Choisissez « Être compris en réunion »',
      content: 'Dans le guide de configuration, choisissez <strong>Être compris en réunion</strong>. Sokuji passe alors en mode <strong>Moi</strong>, avec voix et sous-titres. Si vous avez déjà terminé la configuration, cliquez plutôt sur <strong>Moi</strong> dans le sélecteur de mode, en bas à gauche de la fenêtre principale.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Réglez les deux langues',
      content: 'Réglez <strong>je parle</strong> sur votre propre langue et <strong>ils entendent</strong> sur celle des participants à la réunion. Ici : je parle chinois, ils entendent anglais.',
      screenshot: '02-languages.png',
    },
    {
      title: 'Installez le câble audio virtuel',
      content: 'Votre voix traduite arrive dans la réunion par un microphone virtuel. Sous Windows, il s’agit de VB-CABLE, un pilote gratuit. Tant qu’il est absent, Sokuji propose de l’installer à chaque démarrage : cliquez sur <strong>Install Now</strong>, puis redémarrez Windows si on vous le demande. Ensuite, Sokuji envoie de lui-même sa voix traduite vers VB-CABLE.',
      screenshot: '03-virtual-cable-tip.png',
      tip: 'Sous macOS, le programme d’installation de Sokuji ajoute un pilote audio virtuel, et sous Linux Sokuji crée son propre microphone virtuel. La suite de ce guide est identique.',
    },
    {
      title: 'Choisissez votre microphone',
      content: 'Dans les Paramètres, sous <strong>Microphone</strong>, sélectionnez votre vrai microphone. Dans la section <strong>Voix</strong>, <strong>Traduction entendue par l\'autre</strong> est activée et envoyée au microphone virtuel, <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. <strong>Je l\'entends aussi</strong> est facultatif : activez-le et choisissez votre casque à côté si vous voulez entendre vous-même la voix traduite.',
      screenshot: '04-devices.png',
    },
    {
      title: 'Choisissez VB-CABLE comme microphone dans l’application de réunion',
      content: 'Dans Microsoft Teams, ouvrez <strong>Paramètres → Appareils</strong> et réglez le microphone sur <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. Dans Zoom, le même réglage se trouve sous <strong>Paramètres → Audio → Microphone</strong>. Laissez le haut-parleur sur votre casque ou vos enceintes habituels.',
      tip: 'Votre vrai microphone se choisit dans Sokuji, pas dans l’application de réunion : Sokuji vous écoute, et l’application de réunion ne reçoit que la voix traduite.',
    },
    {
      title: 'Commencez à parler',
      content: 'Cliquez sur <strong>Démarrer</strong> dans Sokuji et parlez dans votre propre langue. Les participants entendent la traduction dans leur langue, et Sokuji affiche ce que vous avez dit et sa traduction en sous-titres.',
      screenshot: '05-ready-to-start.png',
    },
  ],
  tips: {
    title: 'Conseils pour de meilleurs résultats',
    items: [
      'Faites d’abord un court appel de test, par exemple avec un collègue, et vérifiez qu’il entend la voix traduite.',
      'Marquez une courte pause à la fin de chaque phrase : la traduction est prononcée une fois votre idée terminée.',
      'Portez un casque, pour que le son de la réunion n’atteigne pas votre microphone.',
    ],
  },
  faq: {
    title: 'Questions fréquentes',
    items: [
      {
        question: 'Mon interlocuteur doit-il installer quelque chose ?',
        answer: 'Non. Vous êtes le seul à utiliser Sokuji. Votre interlocuteur entend simplement un microphone qui parle sa langue.',
      },
      {
        question: 'Puis-je aussi comprendre ce qu’ils disent ?',
        answer: 'Oui. Passez en mode Les deux et suivez le guide des réunions bidirectionnelles : Sokuji traduit alors aussi l’autre partie pour vous, en sous-titres et, si vous le souhaitez, à voix haute.',
      },
      {
        question: 'Puis-je choisir la voix ?',
        answer: 'Oui. Dans la section Voix, cliquez sur le nom de la voix sous « Traduction entendue par l\'autre » (par exemple Voix Adrian). Cela ouvre Paramètres de Voix, où vous pouvez choisir une autre voix ou cloner la vôtre.',
      },
      {
        question: 'Mon interlocuteur n’entend rien. Que dois-je vérifier ?',
        answer: 'Vérifiez que le microphone de l’application de réunion est CABLE Output (VB-Audio Virtual Cable), que « Traduction entendue par l\'autre » est activée dans Sokuji, et qu’une session est en cours.',
      },
    ],
  },
  troubleshooting: {
    title: 'Dépannage',
    content: 'Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const ar: TutorialData = {
  pageTitle: 'تحدّث في الاجتماعات بلغة أخرى',
  backLink: 'العودة إلى البدء السريع مع KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'نظرة عامة',
    content: 'يُعدّ هذا الدليل تطبيق Sokuji لسطح المكتب بحيث يسمع المشاركون في اجتماعك ما تقوله منطوقًا بلغتهم. تتحدث أنت بلغتك؛ فيترجمه Sokuji وينطقه في الاجتماع عبر ميكروفون افتراضي. يعمل مع أي تطبيق اجتماعات يتيح لك اختيار الميكروفون، بما في ذلك تطبيقَي Microsoft Teams وZoom لسطح المكتب، ويستخدم KizunaAI (مدعوم من Soniox؛ ويعمل محرك Qwen بالطريقة نفسها). لقطات الشاشة مأخوذة من Sokuji 0.44.0 على Windows، مع التحدث بالصينية في اجتماع باللغة الإنجليزية.',
  },
  steps: [
    {
      title: 'اضبط Sokuji مع KizunaAI',
      content: 'إذا لم تفعل ذلك بعد، فاتبع <a href="/docs/kizuna-ai/quick-start">البدء السريع مع KizunaAI</a> لتثبيت Sokuji وتسجيل الدخول.',
    },
    {
      title: 'اختر «كي تُفهَم في اجتماع»',
      content: 'في دليل الإعداد، اختر <strong>كي تُفهَم في اجتماع</strong>. يضع ذلك Sokuji في وضع <strong>أنا</strong> مع الصوت والترجمة النصية. إذا كنت قد أنهيت الإعداد بالفعل، فانقر بدلًا من ذلك على <strong>أنا</strong> في مبدّل الأوضاع أسفل يسار النافذة الرئيسية.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'اضبط اللغتين',
      content: 'اضبط <strong>أتحدث</strong> على لغتك و<strong>يسمعون</strong> على لغة المشاركين في الاجتماع. هنا: أتحدث الصينية، يسمعون الإنجليزية.',
      screenshot: '02-languages.png',
    },
    {
      title: 'ثبّت كابل الصوت الافتراضي',
      content: 'يصل صوتك المترجَم إلى الاجتماع عبر ميكروفون افتراضي. على Windows هذا هو VB-CABLE، وهو برنامج تشغيل مجاني. ما دام غير مثبّت، يطلب Sokuji تثبيته في كل مرة يبدأ فيها: انقر على <strong>Install Now</strong>، وأعد تشغيل Windows إذا طُلب منك ذلك. بعد ذلك يرسل Sokuji صوته المترجَم إلى VB-CABLE تلقائيًا.',
      screenshot: '03-virtual-cable-tip.png',
      tip: 'على macOS يضيف مثبّت Sokuji برنامج تشغيل صوت افتراضيًا، وعلى Linux ينشئ Sokuji ميكروفونه الافتراضي الخاص. بقية هذا الدليل متطابقة.',
    },
    {
      title: 'اختر الميكروفون',
      content: 'في الإعدادات، تحت <strong>ميكروفون</strong>، اختر ميكروفونك الحقيقي. في قسم <strong>الكلام</strong>، يكون خيار <strong>الترجمة التي يسمعها الطرف الآخر</strong> مفعّلًا ويُوجَّه إلى الميكروفون الافتراضي، <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. أما <strong>أسمعها أنا أيضًا</strong> فاختياري: فعّله واختر سماعة الرأس بجانبه إذا أردت أن تسمع الصوت المترجَم بنفسك.',
      screenshot: '04-devices.png',
    },
    {
      title: 'اختر VB-CABLE ميكروفونًا في تطبيق الاجتماع',
      content: 'في Microsoft Teams، افتح <strong>الإعدادات ← الأجهزة (Settings → Devices)</strong> واضبط الميكروفون على <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. في Zoom يوجد الإعداد نفسه تحت <strong>الإعدادات ← الصوت ← الميكروفون (Settings → Audio → Microphone)</strong>. اترك مكبر الصوت على سماعة الرأس أو السماعات المعتادة.',
      tip: 'يُختار ميكروفونك الحقيقي في Sokuji، لا في تطبيق الاجتماع: يستمع Sokuji إليك، ويتلقى تطبيق الاجتماع الصوت المترجَم فقط.',
    },
    {
      title: 'ابدأ التحدث',
      content: 'انقر على <strong>ابدأ</strong> في Sokuji وتحدث بلغتك. يسمع المشاركون في الاجتماع الترجمة بلغتهم، ويعرض Sokuji ما قلته وترجمته كترجمة نصية.',
      screenshot: '05-ready-to-start.png',
    },
  ],
  tips: {
    title: 'نصائح لأفضل النتائج',
    items: [
      'أجرِ أولًا مكالمة اختبار قصيرة، مع زميل مثلًا، وتأكد من أنه يسمع الصوت المترجَم.',
      'توقف لحظة في نهاية كل جملة: تُنطق الترجمة بعد أن تُكمل فكرتك.',
      'ارتدِ سماعات الرأس، حتى لا يصل صوت الاجتماع إلى ميكروفونك.',
    ],
  },
  faq: {
    title: 'الأسئلة الشائعة',
    items: [
      {
        question: 'هل يحتاج الطرف الآخر إلى تثبيت أي شيء؟',
        answer: 'لا. أنت وحدك من يشغّل Sokuji. الطرف الآخر يسمع فقط ميكروفونًا يتحدث بلغته.',
      },
      {
        question: 'هل يمكنني أيضًا فهم ما يقولونه؟',
        answer: 'نعم. انتقل إلى وضع الكل واتبع دليل الاجتماعات ثنائية الاتجاه: عندها يترجم Sokuji كلام الطرف الآخر لك أيضًا، كترجمة نصية، ويقرؤه عليك بصوت مسموع إن أردت.',
      },
      {
        question: 'هل يمكنني اختيار الصوت؟',
        answer: 'نعم. في قسم «الكلام»، انقر على اسم الصوت تحت «الترجمة التي يسمعها الطرف الآخر» (مثلًا «الصوت Adrian»). يفتح ذلك «إعدادات الصوت»، حيث يمكنك اختيار صوت آخر أو استنساخ صوتك.',
      },
      {
        question: 'الطرف الآخر لا يسمع شيئًا. ماذا أتحقق منه؟',
        answer: 'تحقّق من أن ميكروفون تطبيق الاجتماع هو CABLE Output (VB-Audio Virtual Cable)، وأن «الترجمة التي يسمعها الطرف الآخر» مفعّلة في Sokuji، وأن هناك جلسة جارية.',
      },
    ],
  },
  troubleshooting: {
    title: 'استكشاف الأخطاء وإصلاحها',
    content: 'هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const pt: TutorialData = {
  pageTitle: 'Fale em reuniões em outro idioma',
  backLink: 'Voltar ao início rápido com KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Visão geral',
    content:
      'Este guia configura o aplicativo de desktop do Sokuji para que as pessoas na sua reunião ouçam o que você diz, falado no idioma delas. Você fala no seu próprio idioma; o Sokuji traduz e fala a tradução na reunião por meio de um microfone virtual. Funciona com qualquer aplicativo de reunião que permita escolher um microfone, incluindo os aplicativos de desktop do Microsoft Teams e do Zoom, e usa a KizunaAI (Com tecnologia Soniox; o mesmo vale para o mecanismo Qwen). As capturas de tela são do Sokuji 0.44.0 no Windows, falando chinês para uma reunião em inglês.',
  },
  steps: [
    {
      title: 'Configure o Sokuji com a KizunaAI',
      content:
        'Se ainda não fez isso, siga o <a href="/docs/kizuna-ai/quick-start">início rápido com KizunaAI</a> para instalar o Sokuji e entrar na sua conta.',
    },
    {
      title: 'Escolha “Ser entendido em uma reunião”',
      content:
        'No guia de configuração, escolha <strong>Ser entendido em uma reunião</strong>. Isso coloca o Sokuji no modo <strong>Eu</strong>, com voz e legendas. Se você já concluiu a configuração, clique em <strong>Eu</strong> no seletor de modo, no canto inferior esquerdo da janela principal.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Defina os dois idiomas',
      content:
        'Defina <strong>eu falo</strong> como o seu idioma e <strong>eles ouvem</strong> como o idioma das pessoas na reunião. Aqui: eu falo Chinês, eles ouvem Inglês.',
      screenshot: '02-languages.png',
    },
    {
      title: 'Instale o cabo de áudio virtual',
      content:
        'Sua voz traduzida chega à reunião por um microfone virtual. No Windows, é o VB-CABLE, um driver gratuito. Enquanto ele não estiver instalado, o Sokuji oferece instalá-lo toda vez que inicia: clique em <strong>Install Now</strong> e reinicie o Windows se for solicitado. A partir daí, o Sokuji envia sozinho a voz traduzida para o VB-CABLE.',
      screenshot: '03-virtual-cable-tip.png',
      tip: 'No macOS, o instalador do Sokuji adiciona um driver de áudio virtual, e no Linux o Sokuji cria o próprio microfone virtual. O restante deste guia é igual.',
    },
    {
      title: 'Escolha seu microfone',
      content:
        'Em Configurações, em <strong>Microfone</strong>, escolha seu microfone real. Na seção <strong>Voz</strong>, <strong>Tradução que o outro lado ouve</strong> está ativada e vai para o microfone virtual, <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. <strong>Eu também ouço</strong> é opcional: ative essa opção e escolha seu fone de ouvido ao lado dela se quiser ouvir você mesmo a voz traduzida.',
      screenshot: '04-devices.png',
    },
    {
      title: 'Escolha o VB-CABLE como microfone no aplicativo de reunião',
      content:
        'No Microsoft Teams, abra <strong>Configurações → Dispositivos</strong> e defina o microfone como <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. No Zoom, a mesma opção fica em <strong>Configurações → Áudio → Microfone</strong>. Deixe o alto-falante no fone de ouvido ou nas caixas de som que você usa normalmente.',
      tip: 'Seu microfone real é escolhido no Sokuji, não no aplicativo de reunião: o Sokuji escuta você, e o aplicativo de reunião recebe só a voz traduzida.',
    },
    {
      title: 'Comece a falar',
      content:
        'Clique em <strong>Iniciar</strong> no Sokuji e fale no seu idioma. As pessoas na reunião ouvem a tradução no idioma delas, e o Sokuji mostra o que você disse e a tradução como legendas.',
      screenshot: '05-ready-to-start.png',
    },
  ],
  tips: {
    title: 'Dicas para melhores resultados',
    items: [
      'Faça antes uma chamada de teste curta, por exemplo com um colega, e confira se ele ouve a voz traduzida.',
      'Faça uma pausa breve no fim de cada frase: a tradução é falada quando você conclui uma ideia.',
      'Use fones de ouvido, para que o som da reunião não chegue ao seu microfone.',
    ],
  },
  faq: {
    title: 'Perguntas frequentes',
    items: [
      {
        question: 'A outra pessoa precisa instalar alguma coisa?',
        answer: 'Não. Só você usa o Sokuji. A outra pessoa apenas ouve um microfone que fala o idioma dela.',
      },
      {
        question: 'Também consigo entender o que eles dizem?',
        answer: 'Sim. Mude para o modo Ambos e siga o guia de reuniões bidirecionais: o Sokuji então também traduz o outro lado para você, em legendas e, se quiser, em voz alta.',
      },
      {
        question: 'Posso escolher a voz?',
        answer: 'Sim. Na seção Voz, clique no nome da voz abaixo de Tradução que o outro lado ouve (por exemplo, “Voz Adrian”). Isso abre Configurações de Voz, onde você pode escolher outra voz ou clonar a sua.',
      },
      {
        question: 'A outra pessoa não ouve nada. O que devo verificar?',
        answer:
          'Verifique se o microfone do aplicativo de reunião é CABLE Output (VB-Audio Virtual Cable), se Tradução que o outro lado ouve está ativada no Sokuji e se há uma sessão em andamento.',
      },
    ],
  },
  troubleshooting: {
    title: 'Solução de problemas',
    content:
      'Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Você também pode perguntar no <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const ru: TutorialData = {
  pageTitle: 'Говорите на встречах на другом языке',
  backLink: 'Назад к быстрому старту KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Обзор',
    content:
      'Это руководство настраивает настольное приложение Sokuji так, чтобы участники встречи слышали ваши слова на своём языке. Вы говорите на своём языке, а Sokuji переводит сказанное и произносит перевод во встрече через виртуальный микрофон. Это работает с любым приложением для встреч, в котором можно выбрать микрофон, включая настольные приложения Microsoft Teams и Zoom; используется KizunaAI (на базе Soniox; движок Qwen работает так же). Снимки экрана сделаны в Sokuji 0.44.0 на Windows: речь на китайском для англоязычной встречи.',
  },
  steps: [
    {
      title: 'Настройте Sokuji с KizunaAI',
      content:
        'Если вы ещё этого не сделали, следуйте руководству <a href="/docs/kizuna-ai/quick-start">«Быстрый старт с KizunaAI»</a>, чтобы установить Sokuji и войти в учётную запись.',
    },
    {
      title: 'Выберите «Чтобы вас понимали на встрече»',
      content:
        'В мастере настройки выберите <strong>Чтобы вас понимали на встрече</strong>. Sokuji переходит в режим <strong>Я</strong> с голосом и субтитрами. Если вы уже завершили настройку, вместо этого нажмите <strong>Я</strong> в переключателе режимов в левом нижнем углу главного окна.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Задайте два языка',
      content:
        'Укажите в поле <strong>я говорю на</strong> свой язык, а в поле <strong>собеседник слышит</strong> — язык участников встречи. Здесь: я говорю на китайском, собеседник слышит английский.',
      screenshot: '02-languages.png',
    },
    {
      title: 'Установите виртуальный аудиокабель',
      content:
        'Ваш переведённый голос попадает во встречу через виртуальный микрофон. На Windows это VB-CABLE, бесплатный драйвер. Пока он не установлен, Sokuji при каждом запуске предлагает его установить: нажмите <strong>Install Now</strong> и перезапустите Windows, если система попросит. После этого Sokuji сам отправляет переведённый голос в VB-CABLE.',
      screenshot: '03-virtual-cable-tip.png',
      tip: 'На macOS установщик Sokuji добавляет виртуальный аудиодрайвер, а на Linux Sokuji создаёт собственный виртуальный микрофон. Остальная часть руководства та же.',
    },
    {
      title: 'Выберите микрофон',
      content:
        'В настройках, в разделе <strong>Микрофон</strong>, выберите свой настоящий микрофон. В блоке <strong>Речь</strong> параметр <strong>Перевод, который слышит собеседник</strong> включён и выводится в виртуальный микрофон, <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. <strong>Я тоже слышу</strong> включать не обязательно: включите его и выберите рядом свою гарнитуру, если хотите сами слышать переведённый голос.',
      screenshot: '04-devices.png',
    },
    {
      title: 'Выберите VB-CABLE как микрофон в приложении для встреч',
      content:
        'В Microsoft Teams откройте <strong>Настройки → Устройства</strong> (Settings → Devices) и выберите в качестве микрофона <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. В Zoom та же настройка находится в разделе <strong>Настройки → Звук → Микрофон</strong> (Settings → Audio → Microphone). Динамик оставьте на обычной гарнитуре или колонках.',
      tip: 'Настоящий микрофон выбирается в Sokuji, а не в приложении для встреч: Sokuji слушает вас, а приложение для встреч получает только переведённый голос.',
    },
    {
      title: 'Начните говорить',
      content:
        'Нажмите <strong>Старт</strong> в Sokuji и говорите на своём языке. Участники встречи слышат перевод на своём языке, а Sokuji показывает сказанное вами и его перевод в виде субтитров.',
      screenshot: '05-ready-to-start.png',
    },
  ],
  tips: {
    title: 'Советы для лучших результатов',
    items: [
      'Сначала сделайте короткий тестовый звонок, например коллеге, и проверьте, что он слышит переведённый голос.',
      'Делайте короткую паузу в конце каждого предложения: перевод озвучивается, когда вы заканчиваете мысль.',
      'Надевайте наушники, чтобы звук встречи не попадал в ваш микрофон.',
    ],
  },
  faq: {
    title: 'Часто задаваемые вопросы',
    items: [
      {
        question: 'Нужно ли собеседнику что-то устанавливать?',
        answer: 'Нет. Sokuji запускаете только вы. Собеседник просто слышит микрофон, который говорит на его языке.',
      },
      {
        question: 'Могу ли я также понимать, что говорят они?',
        answer: 'Да. Переключитесь в режим «Оба» и следуйте руководству по двусторонним встречам: тогда Sokuji будет также переводить для вас речь другой стороны — в субтитры и, если хотите, вслух.',
      },
      {
        question: 'Можно ли выбрать голос?',
        answer: 'Да. В блоке «Речь» нажмите на название голоса под строкой «Перевод, который слышит собеседник» (например, Голос Adrian). Откроются «Настройки голоса», где можно выбрать другой голос или клонировать свой.',
      },
      {
        question: 'Собеседник ничего не слышит. Что проверить?',
        answer:
          'Проверьте, что в приложении для встреч выбран микрофон CABLE Output (VB-Audio Virtual Cable), что в Sokuji включён «Перевод, который слышит собеседник» и что сеанс запущен.',
      },
    ],
  },
  troubleshooting: {
    title: 'Устранение неполадок',
    content:
      'Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Также можно задать вопрос в <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const de: TutorialData = {
  pageTitle: 'In Meetings in einer anderen Sprache sprechen',
  backLink: 'Zurück zum KizunaAI-Schnellstart',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Übersicht',
    content:
      'Diese Anleitung richtet die Sokuji-Desktop-App so ein, dass die Teilnehmenden Ihres Meetings hören, was Sie sagen, gesprochen in ihrer Sprache. Sie sprechen in Ihrer eigenen Sprache; Sokuji übersetzt es und spricht es über ein virtuelles Mikrofon ins Meeting. Das funktioniert mit jeder Meeting-App, in der Sie ein Mikrofon auswählen können, einschließlich der Desktop-Apps von Microsoft Teams und Zoom, und verwendet KizunaAI (Unterstützt von Soniox; die Qwen-Engine funktioniert genauso). Die Screenshots stammen aus Sokuji 0.44.0 unter Windows und zeigen, wie auf Chinesisch in ein englischsprachiges Meeting gesprochen wird.',
  },
  steps: [
    {
      title: 'Sokuji mit KizunaAI einrichten',
      content:
        'Falls noch nicht geschehen, folgen Sie dem <a href="/docs/kizuna-ai/quick-start">KizunaAI-Schnellstart</a>, um Sokuji zu installieren und sich anzumelden.',
    },
    {
      title: '„In einem Meeting verstanden werden“ wählen',
      content:
        'Wählen Sie in der Einrichtung <strong>In einem Meeting verstanden werden</strong>. Damit wechselt Sokuji in den Modus <strong>Ich</strong>, mit Stimme und Untertiteln. Wenn Sie die Einrichtung bereits abgeschlossen haben, klicken Sie stattdessen im Modusschalter unten links im Hauptfenster auf <strong>Ich</strong>.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Die beiden Sprachen einstellen',
      content:
        'Stellen Sie bei <strong>Ich spreche</strong> Ihre eigene Sprache ein und bei <strong>sie hören</strong> die Sprache der Teilnehmenden im Meeting. Hier: Ich spreche Chinesisch, sie hören Englisch.',
      screenshot: '02-languages.png',
    },
    {
      title: 'Das virtuelle Audiokabel installieren',
      content:
        'Ihre übersetzte Stimme gelangt über ein virtuelles Mikrofon ins Meeting. Unter Windows ist das VB-CABLE, ein kostenloser Treiber. Solange er fehlt, fragt Sokuji bei jedem Start, ob er installiert werden soll: Klicken Sie auf <strong>Install Now</strong> und starten Sie Windows neu, falls Sie dazu aufgefordert werden. Danach sendet Sokuji seine übersetzte Stimme automatisch an VB-CABLE.',
      screenshot: '03-virtual-cable-tip.png',
      tip: 'Unter macOS fügt das Sokuji-Installationsprogramm einen virtuellen Audiotreiber hinzu, und unter Linux erstellt Sokuji ein eigenes virtuelles Mikrofon. Der Rest dieser Anleitung ist gleich.',
    },
    {
      title: 'Mikrofon wählen',
      content:
        'Wählen Sie in den Einstellungen unter <strong>Mikrofon</strong> Ihr echtes Mikrofon. Im Bereich <strong>Sprache</strong> ist <strong>Übersetzung, die das Gegenüber hört</strong> eingeschaltet und geht an das virtuelle Mikrofon, <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. <strong>Ich höre es auch</strong> ist optional: Schalten Sie es ein und wählen Sie daneben Ihr Headset, wenn Sie die übersetzte Stimme selbst hören möchten.',
      screenshot: '04-devices.png',
    },
    {
      title: 'VB-CABLE in der Meeting-App als Mikrofon wählen',
      content:
        'Öffnen Sie in Microsoft Teams <strong>Einstellungen → Geräte</strong> und stellen Sie das Mikrofon auf <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. In Zoom finden Sie dieselbe Einstellung unter <strong>Einstellungen → Audio → Mikrofon</strong>. Lassen Sie den Lautsprecher auf Ihrem üblichen Headset oder Ihren Lautsprechern.',
      tip: 'Ihr echtes Mikrofon wählen Sie in Sokuji aus, nicht in der Meeting-App: Sokuji hört Ihnen zu, und die Meeting-App erhält nur die übersetzte Stimme.',
    },
    {
      title: 'Mit dem Sprechen beginnen',
      content:
        'Klicken Sie in Sokuji auf <strong>Starten</strong> und sprechen Sie in Ihrer eigenen Sprache. Die Teilnehmenden im Meeting hören die Übersetzung in ihrer Sprache, und Sokuji zeigt, was Sie gesagt haben, samt Übersetzung als Untertitel an.',
      screenshot: '05-ready-to-start.png',
    },
  ],
  tips: {
    title: 'Tipps für beste Ergebnisse',
    items: [
      'Machen Sie zuerst einen kurzen Testanruf, zum Beispiel mit einer Kollegin oder einem Kollegen, und prüfen Sie, ob die übersetzte Stimme ankommt.',
      'Machen Sie am Ende jedes Satzes eine kurze Pause: Die Übersetzung wird gesprochen, sobald Sie einen Gedanken abgeschlossen haben.',
      'Tragen Sie Kopfhörer, damit der Ton des Meetings nicht in Ihr Mikrofon gelangt.',
    ],
  },
  faq: {
    title: 'Häufig gestellte Fragen',
    items: [
      {
        question: 'Muss mein Gegenüber etwas installieren?',
        answer: 'Nein. Nur Sie verwenden Sokuji. Ihr Gegenüber hört einfach ein Mikrofon, das seine Sprache spricht.',
      },
      {
        question: 'Kann ich auch verstehen, was die anderen sagen?',
        answer: 'Ja. Wechseln Sie in den Modus „Beides“ und folgen Sie der Anleitung für zweiseitige Meetings: Dann übersetzt Sokuji auch die andere Seite für Sie, als Untertitel und auf Wunsch auch vorgelesen.',
      },
      {
        question: 'Kann ich die Stimme auswählen?',
        answer: 'Ja. Klicken Sie im Bereich „Sprache“ unter „Übersetzung, die das Gegenüber hört“ auf den Namen der Stimme (zum Beispiel „Stimme Adrian“). Damit öffnen sich die „Stimmeinstellungen“, in denen Sie eine andere Stimme wählen oder Ihre eigene klonen können.',
      },
      {
        question: 'Mein Gegenüber hört nichts. Was sollte ich prüfen?',
        answer:
          'Prüfen Sie, ob das Mikrofon der Meeting-App CABLE Output (VB-Audio Virtual Cable) ist, ob „Übersetzung, die das Gegenüber hört“ in Sokuji eingeschaltet ist und ob eine Sitzung läuft.',
      },
    ],
  },
  troubleshooting: {
    title: 'Fehlerbehebung',
    content:
      'Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Sie können auch in den <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> fragen.',
  },
};

const uk: TutorialData = {
  pageTitle: 'Говоріть на зустрічах іншою мовою',
  backLink: 'Назад до швидкого старту KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Огляд',
    content:
      'Цей посібник налаштовує настільний застосунок Sokuji так, щоб учасники зустрічі чули ваші слова своєю мовою. Ви говорите своєю мовою, а Sokuji перекладає сказане й озвучує переклад на зустрічі через віртуальний мікрофон. Це працює з будь-яким застосунком для зустрічей, у якому можна вибрати мікрофон, зокрема з настільними застосунками Microsoft Teams і Zoom; використовується KizunaAI (на базі Soniox; рушій Qwen працює так само). Знімки екрана зроблено в Sokuji 0.44.0 на Windows: мовлення китайською для англомовної зустрічі.',
  },
  steps: [
    {
      title: 'Налаштуйте Sokuji з KizunaAI',
      content:
        'Якщо ви ще цього не зробили, дотримуйтеся посібника <a href="/docs/kizuna-ai/quick-start">«Швидкий старт із KizunaAI»</a>, щоб установити Sokuji та увійти в обліковий запис.',
    },
    {
      title: 'Виберіть «Бути зрозумілим на зустрічі»',
      content:
        'У майстрі налаштування виберіть <strong>Бути зрозумілим на зустрічі</strong>. Sokuji переходить у режим <strong>Я</strong> з голосом і субтитрами. Якщо ви вже завершили налаштування, натомість натисніть <strong>Я</strong> в перемикачі режимів у лівому нижньому куті головного вікна.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Задайте дві мови',
      content:
        'Укажіть у полі <strong>Я говорю</strong> свою мову, а в полі <strong>вони чують</strong> — мову учасників зустрічі. Тут: Я говорю китайською, вони чують англійською.',
      screenshot: '02-languages.png',
    },
    {
      title: 'Установіть віртуальний аудіокабель',
      content:
        'Ваш перекладений голос потрапляє на зустріч через віртуальний мікрофон. На Windows це VB-CABLE, безкоштовний драйвер. Поки його не встановлено, Sokuji під час кожного запуску пропонує його встановити: натисніть <strong>Install Now</strong> і перезапустіть Windows, якщо система попросить. Після цього Sokuji сам надсилає перекладений голос у VB-CABLE.',
      screenshot: '03-virtual-cable-tip.png',
      tip: 'На macOS інсталятор Sokuji додає віртуальний аудіодрайвер, а на Linux Sokuji створює власний віртуальний мікрофон. Решта посібника така сама.',
    },
    {
      title: 'Виберіть мікрофон',
      content:
        'У налаштуваннях, у розділі <strong>Мікрофон</strong>, виберіть свій справжній мікрофон. У блоці <strong>Мовлення</strong> <strong>Переклад, який чує співрозмовник</strong> увімкнено й спрямовано на віртуальний мікрофон, <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. <strong>Я теж чую</strong> вмикати не обов’язково: увімкніть його й виберіть поруч свою гарнітуру, якщо хочете самі чути перекладений голос.',
      screenshot: '04-devices.png',
    },
    {
      title: 'Виберіть VB-CABLE як мікрофон у застосунку для зустрічей',
      content:
        'У Microsoft Teams відкрийте <strong>Налаштування → Пристрої</strong> (Settings → Devices) і виберіть як мікрофон <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. У Zoom те саме налаштування розташоване в розділі <strong>Налаштування → Аудіо → Мікрофон</strong> (Settings → Audio → Microphone). Динамік залиште на звичайній гарнітурі чи колонках.',
      tip: 'Справжній мікрофон вибирається в Sokuji, а не в застосунку для зустрічей: Sokuji слухає вас, а застосунок для зустрічей отримує лише перекладений голос.',
    },
    {
      title: 'Почніть говорити',
      content:
        'Натисніть <strong>Почати</strong> в Sokuji і говоріть своєю мовою. Учасники зустрічі чують переклад своєю мовою, а Sokuji показує сказане вами та його переклад у вигляді субтитрів.',
      screenshot: '05-ready-to-start.png',
    },
  ],
  tips: {
    title: 'Поради для найкращих результатів',
    items: [
      'Спершу зробіть короткий тестовий дзвінок, наприклад колезі, і перевірте, що він чує перекладений голос.',
      'Робіть коротку паузу в кінці кожного речення: переклад озвучується, коли ви завершуєте думку.',
      'Надягайте навушники, щоб звук зустрічі не потрапляв у ваш мікрофон.',
    ],
  },
  faq: {
    title: 'Часті запитання',
    items: [
      {
        question: 'Чи потрібно співрозмовнику щось установлювати?',
        answer: 'Ні. Sokuji запускаєте лише ви. Співрозмовник просто чує мікрофон, який говорить його мовою.',
      },
      {
        question: 'Чи можу я також розуміти, що кажуть вони?',
        answer: 'Так. Перемкніться в режим «Обидва» й дотримуйтеся посібника з двосторонніх зустрічей: тоді Sokuji також перекладатиме для вас мовлення іншої сторони — у субтитрах і, за бажанням, уголос.',
      },
      {
        question: 'Чи можна вибрати голос?',
        answer: 'Так. У блоці «Мовлення» натисніть назву голосу під «Переклад, який чує співрозмовник» (наприклад, Голос Adrian). Відкриються «Налаштування голосу», де можна вибрати інший голос або клонувати власний.',
      },
      {
        question: 'Співрозмовник нічого не чує. Що перевірити?',
        answer:
          'Перевірте, що в застосунку для зустрічей вибрано мікрофон CABLE Output (VB-Audio Virtual Cable), що в Sokuji ввімкнено «Переклад, який чує співрозмовник» і що сеанс запущено.',
      },
    ],
  },
  troubleshooting: {
    title: 'Усунення несправностей',
    content:
      'Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Також можна запитати в <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const it: TutorialData = {
  pageTitle: 'Parla nelle riunioni in un’altra lingua',
  backLink: 'Torna alla guida rapida a KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Panoramica',
    content:
      'Questa guida configura l’app desktop di Sokuji perché le persone nella tua riunione sentano ciò che dici, pronunciato nella loro lingua. Tu parli nella tua lingua; Sokuji lo traduce e lo pronuncia nella riunione tramite un microfono virtuale. Funziona con qualsiasi app per riunioni che ti permetta di scegliere un microfono, comprese le app desktop di Microsoft Teams e Zoom, e usa KizunaAI (Con tecnologia Soniox; il motore Qwen funziona allo stesso modo). Le schermate provengono da Sokuji 0.44.0 su Windows, parlando in cinese in una riunione in inglese.',
  },
  steps: [
    {
      title: 'Configura Sokuji con KizunaAI',
      content:
        'Se non l’hai ancora fatto, segui la <a href="/docs/kizuna-ai/quick-start">guida rapida a KizunaAI</a> per installare Sokuji e accedere.',
    },
    {
      title: 'Scegli “Farsi capire in una riunione”',
      content:
        'Nella configurazione guidata, scegli <strong>Farsi capire in una riunione</strong>. Sokuji passa in modalità <strong>Io</strong>, con voce e sottotitoli. Se hai già completato la configurazione, fai invece clic su <strong>Io</strong> nel selettore di modalità in basso a sinistra nella finestra principale.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Imposta le due lingue',
      content:
        'Imposta <strong>Io parlo</strong> sulla tua lingua e <strong>loro sentono</strong> sulla lingua delle persone nella riunione. Qui: Io parlo cinese, loro sentono inglese.',
      screenshot: '02-languages.png',
    },
    {
      title: 'Installa il cavo audio virtuale',
      content:
        'La tua voce tradotta arriva alla riunione tramite un microfono virtuale. Su Windows è VB-CABLE, un driver gratuito. Finché manca, Sokuji propone di installarlo a ogni avvio: fai clic su <strong>Install Now</strong> e riavvia Windows se richiesto. Da quel momento Sokuji invia da solo la sua voce tradotta a VB-CABLE.',
      screenshot: '03-virtual-cable-tip.png',
      tip: 'Su macOS il programma di installazione di Sokuji aggiunge un driver audio virtuale, e su Linux Sokuji crea un proprio microfono virtuale. Il resto di questa guida è identico.',
    },
    {
      title: 'Scegli il microfono',
      content:
        'In Impostazioni, in <strong>Microfono</strong>, scegli il tuo microfono reale. Nella sezione <strong>Voce</strong>, <strong>Traduzione che sente l\'altra parte</strong> è attiva e va al microfono virtuale, <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. <strong>Lo sento anche io</strong> è facoltativo: attivalo e scegli le tue cuffie accanto se vuoi sentire tu stesso la voce tradotta.',
      screenshot: '04-devices.png',
    },
    {
      title: 'Scegli VB-CABLE come microfono nell’app per riunioni',
      content:
        'In Microsoft Teams, apri <strong>Impostazioni → Dispositivi</strong> e imposta il microfono su <strong>CABLE Output (VB-Audio Virtual Cable)</strong>. In Zoom la stessa impostazione si trova in <strong>Impostazioni → Audio → Microfono</strong>. Lascia l’altoparlante sulle cuffie o sugli altoparlanti che usi di solito.',
      tip: 'Il microfono reale si sceglie in Sokuji, non nell’app per riunioni: Sokuji ascolta te, e l’app per riunioni riceve solo la voce tradotta.',
    },
    {
      title: 'Inizia a parlare',
      content:
        'Fai clic su <strong>Avvia</strong> in Sokuji e parla nella tua lingua. Le persone nella riunione sentono la traduzione nella loro lingua, e Sokuji mostra ciò che hai detto e la sua traduzione come sottotitoli.',
      screenshot: '05-ready-to-start.png',
    },
  ],
  tips: {
    title: 'Consigli per risultati migliori',
    items: [
      'Fai prima una breve chiamata di prova, per esempio con un collega, e verifica che senta la voce tradotta.',
      'Fai una breve pausa alla fine di ogni frase: la traduzione viene pronunciata quando concludi un pensiero.',
      'Indossa le cuffie, così l’audio della riunione non arriva al tuo microfono.',
    ],
  },
  faq: {
    title: 'Domande frequenti',
    items: [
      {
        question: 'L’altra persona deve installare qualcosa?',
        answer: 'No. Solo tu usi Sokuji. L’altra persona sente semplicemente un microfono che parla la sua lingua.',
      },
      {
        question: 'Posso anche capire cosa dicono gli altri?',
        answer: 'Sì. Passa alla modalità Entrambi e segui la guida alle riunioni bidirezionali: Sokuji traduce allora anche l’altra parte per te, come sottotitoli e, se vuoi, letta ad alta voce.',
      },
      {
        question: 'Posso scegliere la voce?',
        answer: 'Sì. Nella sezione Voce, fai clic sul nome della voce sotto Traduzione che sente l\'altra parte (per esempio Voce Adrian). Si apre Impostazioni Voce, dove puoi scegliere un’altra voce o clonare la tua.',
      },
      {
        question: 'L’altra persona non sente niente. Cosa devo controllare?',
        answer:
          'Controlla che il microfono dell’app per riunioni sia CABLE Output (VB-Audio Virtual Cable), che Traduzione che sente l\'altra parte sia attivata in Sokuji e che ci sia una sessione attiva.',
      },
    ],
  },
  troubleshooting: {
    title: 'Risoluzione dei problemi',
    content:
      'Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Puoi anche chiedere su <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function BeUnderstoodTutorial() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/tutorials/be-understood" />;
}
